import { snack, ApButtonLink, ApIcon } from '@alixpartners/ui-components';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import {
  Col as ColFromGrid,
  Container as ContaienrFromGrid,
  Row as RowFromGrid,
} from 'react-grid-system';
import Tippy from '@tippy.js/react';

import { IReport } from '../../../../types';
import { ConfirmationModal } from '../../../../components/ConfirmationModal';

import './ReportBlock.scss';

const Col: any = ColFromGrid;
const Container: any = ContaienrFromGrid;
const Row: any = RowFromGrid;

interface IReportBlockProps {
  report: IReport;
  isPowerBiEnabled: boolean;
  isShareEnabled: boolean;
  handleReportShare: (id: string) => Promise<any>;
  onReportShareDone: () => void;
  onReportClick: (report: IReport) => void;
  onReportTabClick: (report: IReport, tabId: string) => void;
  navigate: NavigateFunction;
}

const containerStyles = { width: '100%' };

const ReportBlock: React.SFC<IReportBlockProps> = ({
  report,
  isPowerBiEnabled,
  isShareEnabled,
  navigate,
  onReportClick,
  onReportTabClick,
  handleReportShare,
  onReportShareDone,
}) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  // const [error, setError] = useState<string>('');

  const componentIsMounted = useRef(true);
  useEffect(() => {
    return () => {
      componentIsMounted.current = false;
    };
  }, []);

  const shareButtonClick = async (confirmed: boolean) => {
    if (report.isShared || !isShareEnabled) {
      return;
    }
    if (!confirmed && report.sharedDate) {
      setShowConfirmationModal(true);
    } else {
      // setError('');
      setIsLoading(true);
      try {
        await handleReportShare(report.id);
        onReportShareDone();
        report.sharedDate = new Date().toString();
        if (componentIsMounted.current) {
          setIsLoading(false);
        }
      } catch (error: any) {
        if (componentIsMounted.current) {
          // setError(
          //   error.message === 'NotStarted'
          //     ? 'Share failed, refresh and try again'
          //     : 'Sorry, there was an error while processing your request'
          // );
          snack.error({
            message:
              error.message === 'NotStarted'
                ? 'Share failed, refresh and try again'
                : 'Sorry, there was an error while processing your request',
          });
          setIsLoading(false);
        }
      }
    }
  };

  const getShareButtonBlock = () => {
    if (report.sharingDetails.isShareable) {
      return isShareEnabled ? (
        <ApButtonLink
          onClick={() => {
            shareButtonClick(false);
          }}
          disabled={isLoading || !isShareEnabled}
        >
          {isLoading ? (
            <span className="report-block__sharing">
              {'Sharing. Please wait'}
            </span>
          ) : (
            'Share'
          )}
        </ApButtonLink>
      ) : (
        <Tippy
          arrow={true}
          placement="top"
          animation="fade"
          boundary="window"
          interactive={true}
          content="Only Approvers may share reports. See above."
        >
          <div className="report-block__share-button">
            <ApButtonLink onClick={() => {}} disabled>
              Share
            </ApButtonLink>
          </div>
        </Tippy>
      );
    } else {
      return (
        <Tippy
          arrow={true}
          placement="top"
          animation="fade"
          boundary="window"
          interactive={true}
          content={
            <span>
              This report uses a live relational database connection, only
              extracts can be shared.{' '}
              <span
                className="report-block__tooltip-link"
                onClick={() => {
                  navigate('/help');
                }}
              >
                See how to publish
              </span>{' '}
              your report as an extract to share.
            </span>
          }
        >
          <div className="report-block__share-button">
            <ApButtonLink onClick={() => {}} disabled>
              Can not be shared
            </ApButtonLink>
          </div>
        </Tippy>
      );
    }
  };

  return (
    <div className="report-block">
      <ConfirmationModal
        isOpen={showConfirmationModal}
        header="Confirm share report"
        onModalClose={() => {
          setShowConfirmationModal(false);
        }}
        onConfirm={() => {
          shareButtonClick(true);
        }}
        confirmText={
          'Current report is already shared. Do you want to overwrite it?'
        }
      />
      <Container fluid style={containerStyles}>
        <Row justify="around">
          <Col xs={isPowerBiEnabled ? 8 : 6}>
            <div
              className={`report-block__collapsible${
                !isCollapsed ? ' report-block__collapsible--expanded' : ''
              }`}
              onClick={() => {
                setIsCollapsed(!isCollapsed);
              }}
            >
              <div className="report-block__collapsible__report-name">
                <div
                  className="link"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isLoading) {
                      return;
                    }
                    onReportClick(report);
                  }}
                >
                  {report.reportName}
                </div>
                <div>
                  <ApIcon iconName="baseline_arrow_drop_down" />
                </div>
              </div>
              {!isCollapsed && (
                <div className="report-block__collapsible__report-pages">
                  {report.reportPages.map((page) => (
                    <div
                      key={page.id}
                      className="link-small"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isLoading) {
                          return;
                        }
                        onReportTabClick(report, page.id);
                      }}
                    >
                      {page.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Col>
          {!isPowerBiEnabled && (
            <Col xs={2}>
              <div className="report-block__date-container">
                {report.sharedDate &&
                  moment(report.sharedDate).format('D MMM YYYY, H:mm a')}
              </div>
            </Col>
          )}
          <Col xs={2}>
            <div className="report-block__date-container">
              {report.sharedDate &&
                moment(report.sharedDate).format('D MMM YYYY, H:mm a')}
            </div>
          </Col>
          <Col xs={2}>
            {report.isShared ? (
              <Tippy
                arrow={true}
                placement="top"
                animation="fade"
                boundary="window"
                content="To re-publish: remove from Shared Reports first."
              >
                <div className="report-block__share-button">
                  <ApButtonLink onClick={() => {}} disabled>
                    Shared
                  </ApButtonLink>
                </div>
              </Tippy>
            ) : (
              getShareButtonBlock()
            )}
          </Col>
        </Row>
        {/* {error && (
          <Row justify="end">
            <Col>
              <div className="report-block__error">{error}</div>
            </Col>
          </Row>
        )} */}
      </Container>
    </div>
  );
};

const ReportBlockWithNavigate = (props: any) => {
  const navigate = useNavigate();

  return <ReportBlock {...props} navigate={navigate} />;
};

export default ReportBlockWithNavigate;
